/**
 * A table whose rows open into more rows: a place, then what is in it, then what
 * is in that.
 *
 * Still a real `<table>` — one row per node, indented by depth — so a reader
 * with assistive technology gets columns and rows, and each row that can open
 * says so (`aria-expanded` on its button) instead of that being drawn.
 *
 * The totals on a parent row are the caller's to compute: this component knows
 * nothing about what the columns mean, only that a row may have children.
 */

import { useState, type ReactNode } from "react";

import "./data.css";

export interface TreeRow<Node> {
  key: string;
  node: Node;
  children?: readonly TreeRow<Node>[];
}

export interface TreeColumn<Node> {
  key: string;
  header: string;
  /** `depth` is 0 for a top-level row. */
  render: (node: Node, depth: number) => ReactNode;
  align?: "start" | "end";
}

export interface TreeTableProps<Node> {
  caption: string;
  columns: readonly TreeColumn<Node>[];
  rows: readonly TreeRow<Node>[];
  /** Spoken on a row's open/close button, e.g. "Show Casablanca". */
  toggleLabel: (node: Node, expanded: boolean) => string;
  /** Start with every row open. */
  defaultExpanded?: boolean;
  /** Called when a row is opened or closed — to load what is under it, say. */
  onToggle?: (row: TreeRow<Node>, open: boolean) => void;
}

export function TreeTable<Node>({
  caption,
  columns,
  rows,
  toggleLabel,
  defaultExpanded = false,
  onToggle,
}: TreeTableProps<Node>) {
  // The keys that differ from the default: opened when closed by default, and the reverse.
  const [flipped, setFlipped] = useState<ReadonlySet<string>>(new Set());
  const isOpen = (key: string) => (flipped.has(key) ? !defaultExpanded : defaultExpanded);
  const flip = (key: string) =>
    setFlipped((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const lines: { row: TreeRow<Node>; depth: number }[] = [];
  const walk = (list: readonly TreeRow<Node>[], depth: number) => {
    for (const row of list) {
      lines.push({ row, depth });
      if (row.children?.length && isOpen(row.key)) walk(row.children, depth + 1);
    }
  };
  walk(rows, 0);

  return (
    <div className="hardauto-table__scroll">
      <table className="hardauto-table hardauto-treetable" data-density="compact">
        <caption className="hardauto-table__caption">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col" data-align={column.align ?? "start"}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map(({ row, depth }) => {
            const expandable = Boolean(row.children?.length);
            const open = isOpen(row.key);
            return (
              <tr key={row.key} data-depth={depth} className="hardauto-treetable__row">
                {columns.map((column, index) => (
                  <td key={column.key} data-align={column.align ?? "start"}>
                    {index === 0 ? (
                      <span
                        className="hardauto-treetable__first"
                        style={{ paddingInlineStart: `${depth * 1.5}rem` }}
                      >
                        {expandable ? (
                          <button
                            type="button"
                            className="hardauto-treetable__toggle"
                            aria-expanded={open}
                            aria-label={toggleLabel(row.node, open)}
                            onClick={() => {
                              flip(row.key);
                              onToggle?.(row, !open);
                            }}
                          >
                            {open ? "▾" : "▸"}
                          </button>
                        ) : (
                          <span className="hardauto-treetable__toggle" aria-hidden="true" />
                        )}
                        {column.render(row.node, depth)}
                      </span>
                    ) : (
                      column.render(row.node, depth)
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
